import { useState, useEffect, useCallback } from 'react';
import { useAuth } from '../../context/AuthContext';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Badge } from '../../components/ui/badge';
import { Button } from '../../components/ui/button';
import {
  Camera, Lightbulb, Clock, Loader2, BookOpen, MapPin, ChevronDown, ChevronUp,
  Sparkles, Beaker, Star
} from 'lucide-react';

const DIFFICULTY_STYLE = {
  easy: 'bg-green-100 text-green-700',
  medium: 'bg-amber-100 text-amber-700',
  challenge: 'bg-red-100 text-red-700',
};

export default function ClassFeedPage() {
  const { api } = useAuth();
  const [photos, setPhotos] = useState([]);
  const [activities, setActivities] = useState([]);
  const [loading, setLoading] = useState(true);
  const [expandedCard, setExpandedCard] = useState(null);
  const [tab, setTab] = useState('photos');

  const fetchData = useCallback(async () => {
    try {
      const [photosRes, activitiesRes] = await Promise.all([
        api.get('/class-photos/my/'),
        api.get('/activity-cards/my/'),
      ]);
      setPhotos(photosRes.data || []);
      setActivities(activitiesRes.data || []);
    } catch (e) {
      console.error('Error fetching class feed:', e);
    } finally {
      setLoading(false);
    }
  }, [api]);

  useEffect(() => { fetchData(); }, [fetchData]);

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-[400px]" data-testid="classfeed-loading">
        <Loader2 className="h-8 w-8 animate-spin text-rsc-navy" />
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="class-feed-page">
      <div>
        <h1 className="text-2xl font-bold text-gray-900 flex items-center gap-2">
          <Camera className="h-6 w-6 text-rsc-navy" />
          Class Feed
        </h1>
        <p className="text-sm text-gray-500 mt-1">See what happened in class and try fun activities at home</p>
      </div>

      {/* Tabs */}
      <div className="flex gap-2 border-b" data-testid="feed-tabs">
        <button
          onClick={() => setTab('photos')}
          className={`pb-2 px-4 text-sm font-medium border-b-2 transition ${tab === 'photos' ? 'border-rsc-navy text-rsc-navy' : 'border-transparent text-gray-400 hover:text-gray-600'}`}
          data-testid="tab-photos"
        >
          <Camera className="h-4 w-4 inline mr-1.5" />Today in Class ({photos.length})
        </button>
        <button
          onClick={() => setTab('activities')}
          className={`pb-2 px-4 text-sm font-medium border-b-2 transition ${tab === 'activities' ? 'border-rsc-navy text-rsc-navy' : 'border-transparent text-gray-400 hover:text-gray-600'}`}
          data-testid="tab-activities"
        >
          <Lightbulb className="h-4 w-4 inline mr-1.5" />Try This at Home ({activities.length})
        </button>
      </div>

      {/* Photos Tab */}
      {tab === 'photos' && (
        photos.length === 0 ? (
          <Card className="border-dashed border-2" data-testid="no-photos">
            <CardContent className="p-12 text-center">
              <Camera className="h-12 w-12 text-gray-300 mx-auto mb-4" />
              <h3 className="font-semibold text-gray-700 mb-2">No Class Photos Yet</h3>
              <p className="text-sm text-gray-500">Photos from your child's classes will appear here after each session.</p>
            </CardContent>
          </Card>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4" data-testid="photos-grid">
            {photos.map(photo => (
              <Card key={photo.id} className="overflow-hidden hover:shadow-lg transition-shadow" data-testid={`photo-${photo.id}`}>
                <div className="h-48 overflow-hidden">
                  <img src={photo.image_url} alt={photo.caption} className="w-full h-full object-cover" />
                </div>
                <CardContent className="p-4">
                  <p className="text-sm font-medium text-gray-900 mb-1">{photo.caption}</p>
                  <div className="flex items-center gap-3 text-xs text-gray-500">
                    <span className="flex items-center gap-1"><BookOpen className="h-3 w-3" />{photo.course_title}</span>
                    <span className="flex items-center gap-1"><MapPin className="h-3 w-3" />{photo.location}</span>
                    <span className="flex items-center gap-1"><Clock className="h-3 w-3" />{new Date(photo.session_date).toLocaleDateString([], { month: 'short', day: 'numeric' })}</span>
                  </div>
                  {photo.instructor && <p className="text-[10px] text-gray-400 mt-1">By {photo.instructor}</p>}
                </CardContent>
              </Card>
            ))}
          </div>
        )
      )}

      {/* Activities Tab */}
      {tab === 'activities' && (
        activities.length === 0 ? (
          <Card className="border-dashed border-2" data-testid="no-activities">
            <CardContent className="p-12 text-center">
              <Lightbulb className="h-12 w-12 text-gray-300 mx-auto mb-4" />
              <h3 className="font-semibold text-gray-700 mb-2">No Activities Yet</h3>
              <p className="text-sm text-gray-500">Fun take-home activities will appear here based on your child's enrolled courses.</p>
            </CardContent>
          </Card>
        ) : (
          <div className="space-y-3" data-testid="activities-list">
            {activities.map(activity => {
              const isExpanded = expandedCard === activity.id;
              return (
                <Card key={activity.id} className="hover:shadow-md transition-shadow" data-testid={`activity-${activity.id}`}>
                  <CardContent className="p-0">
                    <button
                      className="w-full p-4 flex items-start gap-4 text-left"
                      onClick={() => setExpandedCard(isExpanded ? null : activity.id)}
                    >
                      <div className="w-10 h-10 rounded-xl bg-rsc-yellow/20 flex items-center justify-center flex-shrink-0">
                        <Lightbulb className="h-5 w-5 text-rsc-yellow" />
                      </div>
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-2 mb-0.5">
                          <h3 className="font-semibold text-gray-900 text-sm">{activity.title}</h3>
                          <Badge className={`${DIFFICULTY_STYLE[activity.difficulty]} text-[10px] px-1.5 py-0`}>{activity.difficulty}</Badge>
                          <Badge variant="outline" className="text-[10px] px-1.5 py-0">
                            <Clock className="h-2.5 w-2.5 mr-0.5" />{activity.time_minutes} min
                          </Badge>
                        </div>
                        <p className="text-xs text-gray-500">
                          {activity.course_title} — Session {activity.lesson_number}: {activity.lesson_title}
                        </p>
                        {!isExpanded && (
                          <p className="text-xs text-gray-400 mt-1 line-clamp-1">{activity.description}</p>
                        )}
                      </div>
                      <div className="flex-shrink-0">
                        {isExpanded ? <ChevronUp className="h-4 w-4 text-gray-400" /> : <ChevronDown className="h-4 w-4 text-gray-400" />}
                      </div>
                    </button>

                    {isExpanded && (
                      <div className="px-4 pb-4 ml-14 space-y-3 border-t pt-3" data-testid={`activity-detail-${activity.id}`}>
                        <p className="text-sm text-gray-700 leading-relaxed">{activity.description}</p>

                        {activity.materials_needed?.length > 0 && (
                          <div>
                            <p className="text-xs font-semibold text-gray-500 uppercase mb-1">Materials Needed</p>
                            <div className="flex flex-wrap gap-1">
                              {activity.materials_needed.map((m, i) => (
                                <span key={i} className="text-xs px-2 py-0.5 bg-gray-100 text-gray-600 rounded-full">{m}</span>
                              ))}
                            </div>
                          </div>
                        )}

                        {activity.skill_reinforced && (
                          <div className="flex items-center gap-1.5 text-xs text-indigo-600">
                            <Sparkles className="h-3.5 w-3.5" />
                            <span className="font-medium">Skill reinforced:</span> {activity.skill_reinforced}
                          </div>
                        )}
                      </div>
                    )}
                  </CardContent>
                </Card>
              );
            })}
          </div>
        )
      )}
    </div>
  );
}
